import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export default function AdminQueriesPage() {
  const { isAdmin } = useAuth();

  // ── All hooks first — Rules of Hooks forbids calling them after a conditional return ──
  const [queries, setQueries]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [responses, setResponses]   = useState({});
  const [submitting, setSubmitting] = useState({});

  const loadQueries = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('user_queries')
      .select('*, profiles(name, email)')
      .eq('is_resolved', false)
      .order('created_at', { ascending: true });
    setQueries((data ?? []).map(q => ({
      ...q,
      user_name:  q.profiles?.name,
      user_email: q.profiles?.email,
    })));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) loadQueries();
  }, [isAdmin, loadQueries]);

  // ── Guard after hooks ──────────────────────────────────────────────────────────
  if (!isAdmin) return <Navigate to="/" replace />;

  const handleRespond = async (id) => {
    const text = responses[id]?.trim();
    if (!text) return;
    setSubmitting((s) => ({ ...s, [id]: true }));
    const { error } = await supabase
      .from('user_queries')
      .update({ admin_response: text, is_resolved: true, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      alert('Failed to submit response. Please try again.');
    } else {
      setQueries((prev) => prev.filter((q) => q.id !== id));
      setResponses((r) => { const copy = { ...r }; delete copy[id]; return copy; });
    }
    setSubmitting((s) => ({ ...s, [id]: false }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Unresolved Queries</h2>
          <p className="text-gray-400 text-sm mt-0.5">
            {queries.length === 0
              ? 'All caught up!'
              : `${queries.length} quer${queries.length === 1 ? 'y' : 'ies'} awaiting response`}
          </p>
        </div>
        <button
          onClick={loadQueries}
          className="px-4 py-2 text-sm text-indigo-600 border border-indigo-200 hover:bg-indigo-50 rounded-lg transition-colors font-medium"
        >
          Refresh
        </button>
      </div>

      {queries.length === 0 ? (
        <div className="text-center py-24 text-gray-400">
          <p className="text-5xl mb-4">✅</p>
          <p className="text-lg font-semibold text-gray-500">All queries resolved</p>
          <p className="text-sm mt-1">Great work — the queue is empty.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {queries.map((q) => (
            <div key={q.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs">
                  {q.user_name?.[0]?.toUpperCase() ?? '?'}
                </div>
                <span className="text-sm font-semibold text-gray-800">{q.user_name}</span>
                <span className="text-gray-400 text-xs">·</span>
                <span className="text-gray-400 text-xs">{q.user_email}</span>
                <span className="text-gray-400 text-xs ml-auto">
                  {new Date(q.created_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })}
                </span>
              </div>

              <p className="text-gray-800 text-sm leading-relaxed bg-gray-50 rounded-lg p-3.5 mb-4">
                {q.query_text}
              </p>

              <div className="space-y-2.5">
                <textarea
                  value={responses[q.id] ?? ''}
                  onChange={(e) =>
                    setResponses((r) => ({ ...r, [q.id]: e.target.value }))
                  }
                  placeholder="Write your response here…"
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition text-sm"
                />
                <button
                  onClick={() => handleRespond(q.id)}
                  disabled={!responses[q.id]?.trim() || submitting[q.id]}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  {submitting[q.id] ? 'Submitting…' : 'Submit Response & Resolve'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
